import React, {useState, useRef, useEffect, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  Alert,
  Platform,
  TextInput,
  Pressable,
  Linking,
  Image,
  NativeSyntheticEvent,
  TextLayoutEventData,
} from 'react-native';
import ReadMore from 'react-native-read-more';
import FastImage from 'react-native-fast-image';
import Animated from 'react-native-reanimated';
import {Gesture, GestureDetector, GestureHandlerRootView} from 'react-native-gesture-handler';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useNavigation, useFocusEffect, useIsFocused} from '@react-navigation/native';
import moment from 'moment-timezone';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import MapView, {Marker, PROVIDER_GOOGLE} from 'react-native-maps';
import {BottomSheetModalProvider, BottomSheetModal} from '@gorhom/bottom-sheet';
import {Dialog} from 'react-native-simple-dialogs';
import {Notifier, NotifierComponents} from 'react-native-notifier';
import firestore from '@react-native-firebase/firestore';
import Share from 'react-native-share';
import {PERMISSIONS, request} from 'react-native-permissions';
import RNCalendarEvents from 'react-native-calendar-events';

import {Event} from '~types/api/event';
import {useTheme} from '~contexts/ThemeContext';
import {spacing, typography, borderRadius} from '~constants';
import {haptics} from '~Utils/haptics';
import {useTranslation} from 'react-i18next';
import LikeButton from '~components/LikeButton';
import {NavigationProps} from '~types/navigation/navigation.type';
import {SCREENS} from '~constants';
import useTabBar from '~containers/Core/navigation/AppScreens/zustand';
import {useGetEventAttendees} from '~hooks/event/useGetEventAttendees';
import {useGetEventUserStatus} from '~hooks/event/useGetEventUserStatus';
import {useAddUserEventStatus} from '~hooks/event/useAddUserEventStatus';
import {useDeleteUserEventStatus} from '~hooks/event/useDeleteUserEventStatus';
import {useGetUserAccount} from '~hooks/user/useGetUser';
import {useGetBusinessAccount} from '~hooks/business/useGetBusinessAccount';
import {useGetEventMatchingStatus} from '~hooks/event/useGetEventMatchingStatus';
import {useGetCommentByEvent} from '~hooks/event/useGetCommentByEvent';
import {useCreateComment} from '~hooks/event/useCreateComment';
import {useDeleteEvent} from '~hooks/event/useDeleteEvent';
import {useCancelEvent} from '~hooks/event/useCancelEvent';
import {useUpdateUserPostCode} from '~hooks/user/useUpdateUserPostcode';
import {useCreateShortUrl} from '~hooks/event/useCreateShortUrl';
import useMatchingLoadingModalAnimation from '~hooks/react-hooks/useMatchingLoadingModalAnimation';
import {SUBSCRIPTION_STATUS, USER_EVENT_STATUS, User} from '~types/api/user';
import {Business} from '~types/api/business';
import {openLocationInMaps} from '~Utils/openLocationInMaps';
import ModernSpinner from '~components/ModernSpinner';
import Button from '~components/Button';
import CommentSheet from '~components/CommentSheet';
import Calendar from '~components/Calendar';
import QRCodeGenerator from '~components/QR-codes/QRCodeGenerator';
import ScannerQRCode from '~components/QR-codes/ScannerQRCode';
import AnimatedLoadingModal from '~components/Matching/AnimatedLoadingModal';
import IssueModal from '~components/IssueModal';
import ModalWithJoin from '~components/ModalWithItems/ModalWithJoin';
import PayPerMatchModal from '~components/PayPerMatchModal';
import PayPerMatchCongratulationModal from '~components/PayPerMatchCongratulationModal';
import {getMapStyle} from '~constants/locationMap';
import {getMessageOfRecurrence} from '~Utils/event';
import FirebaseChatsService from '~services/FirebaseChats';
import {ChatType} from '~types/chat';
import {BigCheckIcon, BigPendingIcon, CheckIcon} from '~assets/icons';
import {useEventCreationStore} from '~providers/eventCreation/zustand';
import {useGetEventSubcategories} from '~hooks/event/useGetEventSubcategories';
import {useQuery} from 'react-query';
import {useUserStore} from '~providers/userStore/zustand';
import {BASE_API_URL, GOOGLE_API_KEY} from '@env';
import axios from 'axios';
import {t} from 'i18next';
import {logScreenView, logEventPageViewed, logEventSignupStarted} from '~Utils/firebaseAnalytics';
import {useAddUserListPayPerMatch} from '~hooks/event/useAddUserListPayPerMatch';
import {useGetEventPayperMatchStatus} from '~hooks/event/useGetEventPayperMatchStatus';
import {useBlockUser, useUnblockUser} from '~hooks/user/useBlockUser';
import {useGetBlockStatus} from '~hooks/user/useGetBlockStatus';
import {PyxiSelectStatusResponse, useGetPyxiSelectQuizStatus} from '~hooks/pyxiselect/useGetPyxiSelectQuizStatus';
import apiService from '~services/ApiService';
import { useSubscriptionContext } from '~contexts/SubscriptionContext';

const {width: SCREEN_WIDTH, height: SCREEN_HEIGHT} = Dimensions.get('window');
const HEADER_HEIGHT = SCREEN_HEIGHT * 0.4; // Reduced from 50% to 35%
const TRUNCATE_DESCRIPTION_NUMBER_OF_LINES = 6;
const DATE_FORMAT = 'ddd MMM DD, YYYY';

// Helper function for random items
const getRandomItem = (arr: Array<any>) => {
  return arr[Math.floor(Math.random() * arr.length)];
};

// Random user arrays for fake attendees
const randomUserArray1 = [
  require('../../../../assets/images/user1.png'),
  require('../../../../assets/images/user2.png'),
  require('../../../../assets/images/user3.png'),
  require('../../../../assets/images/user4.png'),
  require('../../../../assets/images/user5.png'),
  require('../../../../assets/images/user6.png'),
  require('../../../../assets/images/user7.png'),
  require('../../../../assets/images/user8.png'),
];

const randomUserArray2 = [
  require('../../../../assets/images/user9.png'),
  require('../../../../assets/images/user10.png'),
  require('../../../../assets/images/user11.png'),
  require('../../../../assets/images/user12.png'),
  require('../../../../assets/images/user13.png'),
  require('../../../../assets/images/user14.png'),
  require('../../../../assets/images/user15.png'),
];

interface CompleteModernEventDetailProps {
  event: Event;
  onClose: (isBarDisable?: boolean) => void;
  isFromSheet?: boolean;
}


const CompleteModernEventDetail: React.FC<CompleteModernEventDetailProps> = ({event, onClose, isFromSheet = false}) => {
  const {colors, isDarkMode} = useTheme();
  const {top} = useSafeAreaInsets();
  const navigation = useNavigation<NavigationProps>();
  const {setIsTabBarDisabled} = useTabBar();
  const {t} = useTranslation();
  const { hasActivePlan, activePlan } = useSubscriptionContext();

  // State management
  const [lengthMore, setLengthMore] = useState(false);
  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [spinner, setIsSpinner] = useState(false);
  const [isAnimationVisible, setAnimationVisisble] = useState(false);
  const [eventCountry, setEventCountry] = useState('');
  const [pincodeDialogVisible, setPinCodeDialog] = useState(false);
  const [postCode, setPostCode] = useState('');
  const [randomUserImg1, setRandomUserImg1] = useState<any>();
  const [payPerMatchModalVisible, setPayPerMatchModalVisible] = useState(false);
  const [payPerMatchCongratulationModalVisible, setPayPerMatchCongratulation] = useState(false);
  const [payPerMatchLoading, setPayPerMatchLoading] = useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [quizAfterDateSelection, setQuizAfterDateSelection] = useState(false);

  // Set event country on mount and initialize fake attendees
  useEffect(() => {
    // Initialize random user image for fake attendee
    setRandomUserImg1(getRandomItem(randomUserArray1));
  }, []);

  // Focus state management
  const isFocused = useIsFocused();

  // Refs
  const calendarRef = useRef<{open: () => void; close: () => void}>();
  const matchingOptionsSheetRef = useRef<BottomSheetModal>(null);
  const commentSheetRef = useRef<BottomSheetModal>(null);
  const mapRef = useRef<MapView>(null);

  // API Hooks - exactly like old implementation
  const {data: pyxiHost} = useGetBusinessAccount('CmCCvWvpRVhhFlHdgU9QE6hbun82');
  const {
    data: eventAttendees,
    refetch: refetchAttendees,
    isLoading: attendeesLoading,
  } = useGetEventAttendees({
    event_id: event.event_id,
    subscription_status: SUBSCRIPTION_STATUS.ACCEPTED,
  });
  const {data: matchingStatus, refetch: refetchMatchinEventSatus} = useGetEventMatchingStatus(event.event_id + '');
  const {
    data: commentList,
    refetch: refetchCommentEvent,
    isLoading: isCommentEventLoading,
  } = useGetCommentByEvent(event.event_id);
  const {mutateAsync: createComment} = useCreateComment();

  // Get current user ID safely - MOVED UP BEFORE HOOKS
  const zustandUser = useUserStore(state => state.user);
  const currentUserId = zustandUser?.uid || '';

  console.log('🔍 DEBUG: currentUserId before hooks:', currentUserId);

  const {
    data: userStatus,
    isLoading: userStatusIsLoading,
    isFetching: userStatusIsFetching,
    refetch,
  } = useGetEventUserStatus({event_id: event.event_id, user_id: currentUserId || ''});
  const {mutateAsync: addEventUserStatus} = useAddUserEventStatus();
  const {mutateAsync: addEventUserListPayPerMatchStatus} = useAddUserListPayPerMatch();
  const {data: userAccount, refetch: refetchUser} = useGetUserAccount(currentUserId);

  console.log('🔍 DEBUG: userAccount after hook:', userAccount);
  const {data: businessAccount, refetch: refetchBusiness} = useGetBusinessAccount(currentUserId || '');
  const {data: hostUser} = useGetUserAccount(event?.host_id || '');
  const {data: hostBusiness} = useGetBusinessAccount(event?.host_id || '');
  const {mutateAsync: deleteEvent} = useDeleteEvent();
  const {mutateAsync: cancelAEvent} = useCancelEvent();
  const {mutateAsync: deleteEventUserStatus} = useDeleteUserEventStatus();
  const {mutateAsync: updateUserMutation} = useUpdateUserPostCode();
  const {data: categoryData} = useGetEventSubcategories(event.event_id);
  const {setSubCategory} = useEventCreationStore();
  const {createShortUrl} = useCreateShortUrl();
  const {
    data: userStatusPayPerMatch,
    isLoading: userStatusIsLoadingPayPerMatch,
    isFetching: userStatusIsFetchingPayPerMatch,
    refetch: refetchPayperMatchStatus,
  } = useGetEventPayperMatchStatus({event_id: event.event_id});
  const isJoied = userStatusPayPerMatch?.user_id;

  // Block user functionality
  const {mutateAsync: blockUser, isLoading: isBlockingUser} = useBlockUser();
  const {mutateAsync: unblockUser, isLoading: isUnblockingUser} = useUnblockUser();

  console.log(activePlan, 'activePlanactivePlan');
  


const isWithin12HoursOfStart = (startDate: string | undefined, isPyxiSelect: boolean): boolean => {
  if (!startDate) {
    return false;
  }

  // Explicitly use local time for 'now'
  const now = moment().local();
  const eventStart = moment(startDate);

  // Check if the parsed date is valid
  if (!eventStart.isValid()) {
    console.warn(`Invalid date provided: ${startDate}`);
    return false;
  }

  // Calculate difference in milliseconds for precision
  const diffInMs = eventStart.diff(now);
  const diffInHours = diffInMs / (1000 * 60 * 60); // Convert to hours

  // For subscribed users: 2 hours cutoff, for non-subscribed: 12 hours cutoff
  const hoursLimit = hasActivePlan ? (activePlan?.plan.last_minute_join_hours || 2) : 12;
    console.warn(`diffInHoursdiffInHoursdiffInHours: ${hoursLimit}`,diffInHours, diffInHours >= 0 && diffInHours <= hoursLimit);

  return diffInHours >= 0 && diffInHours <= hoursLimit && isPyxiSelect;
};

  // Matching hooks
  const {
    openMatchingLoadingModal,
    setCurrentMatchingEvent,
    setDomain,
    setRefresh,
    setMatchingType,
    matchingType,
    isMatchingLoadingModalVisible,
    matchingModalCallback,
    noMatchesButtonIsVisible,
    closeMatchingLoadingModal,
  } = useMatchingLoadingModalAnimation();

  const user = !(userAccount as unknown as {details: string})?.details ? userAccount : businessAccount;

  // Fixed host determination logic
  const host = React.useMemo(() => {
    // Prefer user account if available and valid (not an error response)
    if (
      hostUser &&
      typeof hostUser === 'object' &&
      hostUser.uid &&
      !(hostUser as unknown as {detail: string})?.detail
    ) {
      console.log('✅ Using host user account:', hostUser.first_name);
      return hostUser;
    }

    // Fall back to business account if available and valid (not an error response)
    if (
      hostBusiness &&
      typeof hostBusiness === 'object' &&
      hostBusiness.uid &&
      !(hostBusiness as unknown as {detail: string})?.detail
    ) {
      console.log('✅ Using host business account:', hostBusiness.name);
      return hostBusiness;
    }

    console.log('❌ No valid host data found');
    return null;
  }, [hostUser, hostBusiness]);

  // Block status hook - needs to be after host definition
  const {data: blockStatus, refetch: refetchBlockStatus} = useGetBlockStatus(host?.uid || '');

  const isPyxiSelect = host?.uid === 'CmCCvWvpRVhhFlHdgU9QE6hbun82';
  const isUserEvent = event?.host_id === currentUserId;
  const isEventPassed = moment(event?.end_date).isBefore(moment());

  // Debug authentication and host data - UPDATED
  console.log('🔍 Auth & Host Debug Info:', {
    currentUserId: currentUserId || 'NULL/UNDEFINED',
    isAuthenticated: !!currentUserId,
    eventHostId: event?.host_id,
    hostUser: hostUser ? 'Found' : 'Not found',
    hostBusiness: hostBusiness ? 'Found' : 'Not found',
    finalHost: host ? 'Found' : 'Not found',
    hostName: host ? (host as Business)?.name || (host as User)?.first_name : 'No name',
    hostUserDetail: (hostUser as unknown as {detail: string})?.detail,
    hostBusinessDetail: (hostBusiness as unknown as {detail: string})?.detail,
    hostUserData: hostUser,
    hostBusinessData: hostBusiness,
    finalHostData: host,
  });

  const publicDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
  const userDomain = zustandUser?.email?.split('@')[1];
  const isPublicDomain = publicDomains.includes(userDomain || '');

  // Groups query for matching
  const {data: groups} = useQuery<any[]>('joined-groups', async () => {
    const token = useUserStore.getState().token;
    const config = {
      headers: {Authorization: token, Accept: 'application/json'},
    };
    const response = await axios.get(BASE_API_URL + 'community/joined', config);
    return response.data;
  });

  // Screen analytics and lifecycle
  useEffect(() => {
    logScreenView('Event Detail', 'EventDetail');
    if (event) {
      logEventPageViewed(event.event_id.toString(), event.event_service_type || 'unknown', 'Modern Event Detail');
    }
  }, [event]);

  // Tab bar management
  React.useLayoutEffect(() => {
    setIsTabBarDisabled(true);
  }, [setIsTabBarDisabled]);

  // Calendar permissions for Android
  useEffect(() => {
    if (Platform.OS === 'android') {
      request(PERMISSIONS.ANDROID.READ_CALENDAR).then(result => {
        console.log(`android calendar read permission: ${result}`);
      });
    }
  }, []);

  // Country detection from coordinates
  const getCountryFromLatLng = useCallback(async () => {
    try {
      const response = await axios.get(
        `${BASE_API_URL}/google-geocode/reverse?latitude=${event?.coords?.lat}&longitude=${event?.coords?.long}&language=en`,
      );
      const result = response.data.results[0];
      const country = result.address_components.find((component: any) => component.types.includes('country')).long_name;
      setEventCountry(country);
    } catch (error) {
      console.error('Error fetching country:', error);
    }
  }, [event?.coords?.lat, event?.coords?.long]);

  useEffect(() => {
    if (event?.coords?.lat) {
      getCountryFromLatLng();
    }
  }, [event?.coords?.lat, getCountryFromLatLng]);

  // Focus-based data refetching
  useEffect(() => {
    if (isFocused) {
      refetchMatchinEventSatus();
      refetchAttendees();
      refetch();
      refetchUser();
      refetchBusiness();
      refetchPayperMatchStatus();
    }
  }, [isFocused, refetchMatchinEventSatus, refetchAttendees, refetch, refetchUser, refetchBusiness]);

  // Focus effect for additional refetching
  useFocusEffect(
    React.useCallback(() => {
      refetchAttendees();
    }, [refetchAttendees]),
  );

  const styles = createStyles(colors);
  // Normalize user status for safe comparisons
  const statusStr = String((userStatus as any)?.status || '').toLowerCase();

  // Handle close
  const handleCloseWithAnimation = useCallback(() => {
    onClose();
  }, [onClose]);

  // Event handlers
  const handleSharePress = async () => {
    if (!event?.event_id) {
      return;
    }
    const userName = (user as User)?.last_name
      ? (user as User)?.first_name + ' ' + (user as User)?.last_name
      : (user as User)?.first_name || (user as Business)?.name;
    const redirectLink = `https://partner.pyxi.ai/event/detail/${event.event_id}`;
    const url = redirectLink;
    await Share.open({
      message: t('invite_message', {userName, itemName: event.name, url}),
    });
  };

  const handleOpenMaps = () => {
    if (!event?.coords?.lat || !event?.coords?.long) {
      return;
    }
    openLocationInMaps({lat: event.coords.lat, long: event.coords.long, pointLabel: event?.address_name});
  };

  // Join/Leave Event Logic - exactly like old implementation
  const onNotificationAboutSubscriptionOn = async (item: any, host: any, responseData: any) => {
    setTimeout(async () => {
      if (responseData.status === USER_EVENT_STATUS.PENDING) {
        Notifier.showNotification({
          title: 'Please wait for the host to accept your request.',
          Component: NotifierComponents.Alert,
          componentProps: {
            alertType: 'info',
          },
        });
        return;
      } else if (responseData.status === 'waiting') {
        Notifier.showNotification({
          title: 'You are in waitlist. Please wait for the host to accept your request.',
          Component: NotifierComponents.Alert,
          componentProps: {
            alertType: 'info',
          },
        });
      } else {
        Notifier.showNotification({
          title: 'All Set! You are Ready for the event.',
          Component: NotifierComponents.Alert,
          componentProps: {
            alertType: 'success',
          },
        });
        refetchAttendees();
        if (item?.host_id != 'wLJLEn8J6oN9RpPyep2BjdnagcA2') {
          setAnimationVisisble(true);
        }
      }
    }, 2000);
  };

  const onNotificationAboutSubscriptionOff = async (item: any, host: any) => {
    setTimeout(async () => {
      Notifier.showNotification({
        title: 'We notified the organiser you are no longer going to the event.',
        Component: NotifierComponents.Alert,
        componentProps: {
          alertType: 'success',
        },
      });
      try {
        // Skip Firestore operations on web platform
        if (Platform.OS !== 'web') {
          const eventOwnerDoc = await firestore().collection('users').doc(host.uid).get();
          const eventOwnerData = eventOwnerDoc.data();
          if (eventOwnerData && eventOwnerData.deviceFcmToken) {
            await FirebaseChatsService.sendPushNotification(
              eventOwnerData.deviceFcmToken,
              'New Event Notification',
              `User ${userAccount?.first_name} are no longer going to the event ${item?.name}`,
              {
                clickAction: 'OPEN_NOTIFICATIONS',
                eventId: item.event_id,
                item: JSON.stringify(item),
              },
            );
          }
        }
      } catch (error) {
        console.error('Error fetching event owner data:', error);
      }
    }, 2000);
  };

  const joinEvent = async (event_id?: number, isLeave?: boolean) => {
    // Check authentication first
    const hourLimit = hasActivePlan ? (activePlan?.plan.last_minute_join_hours || 2) : 12;
    
    if (isWithin12HoursOfStart(event?.start_date, isPyxiSelect) && !event?.event_group_id) {
      Alert.alert('Event Unavailable', `This event is no longer accepting registrations as it starts within ${hourLimit} hours.`);
      return;
    }

    // If this is a Pyxi Select event, ensure required quiz fields are completed
    if (isPyxiSelect) {
      const form_type = event.event_service_type == 'THE_REST_TABLE' ? 'reset_table' : 
                        event.event_service_type == 'HOUSEWARMING' ? 'housewarming' : 'cultural_edit';
      
      console.log('🔍 DEBUG QUIZ CHECK:', {
        event_service_type: event.event_service_type,
        form_type: form_type,
        isPyxiSelect: isPyxiSelect
      });

      const dataPyxiStatus = await apiService.getWithParams<PyxiSelectStatusResponse>('pyxi-select/quiz/status', {
        form_type: form_type,
      });

      const missing = dataPyxiStatus?.not_filled || [];
      console.log('🔍 QUIZ STATUS RESPONSE:', {
        missing: missing,
        dataPyxiStatus: dataPyxiStatus,
        form_type: form_type,
        hasAnyMissing: missing.length > 0
      });

      if (missing.length > 0) {
        console.log('🔍 QUIZ INCOMPLETE - NAVIGATING TO QUIZ:', {
          missing: missing,
          form_type: form_type,
          fromEvent: true,
          eventId: event.event_id
        });

        // If this event is recurring and no specific date was chosen, ask for date first
        if (event?.event_group_id && !event_id) {
          setQuizAfterDateSelection(true);
          calendarRef.current?.open();
          return;
        }

        if (isFromSheet) {
          onClose();
        }
        navigation.navigate(SCREENS.PYXI_SELECT_QUIZ as any, {
          form_type: form_type,
          fromEvent: true,
          event: event,
        });
        return;
      } else {
        console.log('🔍 QUIZ COMPLETE - PROCEEDING WITH JOIN');
      }
    }

    if (!currentUserId) {
      console.error('❌ Cannot join event: User not authenticated');
      Alert.alert('Authentication Error', 'Please log in to join events.');
      return;
    }

    console.log(user?.onboarding_answers, 'useruseruseruser');

    /* if (!user?.onboarding_answers || user.onboarding_answers.length === 0) {
      if (isFromSheet) {
        onClose();
      }
      navigation.navigate(SCREENS.EDIT_PREFERANCE, {setting: true});
      return;
    } */

    // If this is a recurring event and no specific event_id is provided, show the calendar
    // But if event_id is provided (user selected from upcoming dates), proceed directly
    if (event?.event_group_id && !event_id) {
      calendarRef.current?.open();
      return;
    }

    if (event && !event?.payment_url) {
      if (statusStr !== 'accepted') {
        console.log(event, 'eventeventevent BUY_TICKET');
        if (isFromSheet) {
          onClose();
        }
        navigation.navigate(SCREENS.BUY_TICKET, {eventId: event_id ? event_id : event.event_id});
        return;
      } else {
        console.log(event, 'eventeventevent ');
        if (isFromSheet) {
          onClose();
        }
        if (event_id ===  event.event_id) {
        navigation.navigate(SCREENS.PAYMENT_SUCCESS, {order_id: (userStatus?.order_id || '') + ''});
        } else {
        navigation.navigate(SCREENS.BUY_TICKET, {eventId: event_id ? event_id : event.event_id});
        }
        return;
      }
    }

    if (event?.payment_url && statusStr !== 'accepted') {
      setModalIsVisible(true);
    }

    if (!event) {
      return;
    }
    if (event_id) {
      if (!isLeave) {
        const data = await addEventUserStatus({
          event_id: event_id.toString(),
        });
        if (!event?.is_paid) {
          statusStr === 'accepted'
            ? onNotificationAboutSubscriptionOff(event, host)
            : onNotificationAboutSubscriptionOn(event, host, data);
        }

        if (event.event_type === 'business' || event.private) {
          await FirebaseChatsService.addNewUserToTheGroupChat({
            user_id: currentUserId,
            user_name: `${(user as User)?.first_name || ''} ${(user as User)?.last_name || ''}`,
            user_image: user!.photo,
            event_id: event_id,
          });
        }
      } else {
        await deleteEventUserStatus({event_id: event_id, user_id: currentUserId});
        await FirebaseChatsService.removeUserFromTheGroupChat({
          user_id: currentUserId,
          user_name: `${(user as User)!.first_name || ''} ${(user as User)!.last_name || ''}`,
          event_id: event_id,
          event_image: event.image_url,
        });
      }
    } else {
      if (!userStatus) {
        const data = await addEventUserStatus({
          event_id: event.event_id.toString(),
        });
        if (!event?.is_paid) {
          statusStr === 'accepted'
            ? onNotificationAboutSubscriptionOff(event, host)
            : onNotificationAboutSubscriptionOn(event, host, data);
        }

        if (event.event_type === 'business' || event.private) {
          await FirebaseChatsService.addNewUserToTheGroupChat({
            user_id: currentUserId,
            user_name: `${(user as User)?.first_name || ''} ${(user as User)?.last_name || ''}`,
            user_image: user!.photo,
            event_id: event.event_id,
          });
        }
      } else {
        await deleteEventUserStatus({event_id: event.event_id, user_id: currentUserId});
        await FirebaseChatsService.removeUserFromTheGroupChat({
          user_id: currentUserId,
          user_name: `${(user as User)!.first_name || ''} ${(user as User)!.last_name || ''}`,
          event_id: event.event_id,
          event_image: event.image_url,
        });
      }
    }
    if (event_id) {
      if (event_id !== event.event_id) {
        if (isFromSheet) {
          onClose();
        }
        navigation.replace(SCREENS.HOME_EVENT, {eventId: event_id});
      } else {
        await refetch();
      }
    } else {
      await refetch();
    }
  };

  const handlePayPerMatchClose = () => {
    setPayPerMatchModalVisible(false);
    refetch();
  };

  const onGetPayPerMatchTicket = () => {
    setPayPerMatchCongratulation(false);
    setTimeout(() => {
      setPayPerMatchModalVisible(true);
    }, 300);
  };

  const handlePayPerMatchCongratulationClose = () => {
    setPayPerMatchCongratulation(false);
    refetchPayperMatchStatus();
  };

  const onFindSomeonePayForMatch = () => {
    setPayPerMatchCongratulation(false);
    if (isFromSheet) {
      onClose();
    }
    navigation.navigate(SCREENS.LOGISTIC_OPTION, {event: event, userStatus: userStatus});
  };

  const onJoinEventClick = async () => {
    if (event) {
      logEventSignupStarted(event.event_id.toString(), event.event_service_type || 'unknown', 'Modern Event Detail');
    }
    const hourLimit = hasActivePlan ? (activePlan?.plan.last_minute_join_hours || 2) : 12;
    if (isWithin12HoursOfStart(event?.start_date, isPyxiSelect) && !event?.event_group_id) {
      Alert.alert('Event Unavailable', `This event is no longer accepting registrations as it starts within ${hourLimit} hours.`);
      return;
    }
    // Check if this is a pay-per-match event
    if (event.pay_per_match) {
      setPayPerMatchCongratulation(true);
      try {
        await addEventUserListPayPerMatchStatus({
          event_id: event.event_id.toString(),
        });
        refetchPayperMatchStatus();
      } catch (error) {}
      return;
    }

    if (!userStatus && eventCountry === 'Greece') {
      if (userAccount?.postal_code) {
        joinEvent();
      } else {
        setPinCodeDialog(true);
      }
    } else {
      joinEvent();
    }
  };

  // Add to Calendar functionality - exactly like old implementation
  const addEventToCalendar = async (title: any, startDate: any, endDate: any, description: any, location: any) => {
    const now = new Date();
    const startDateEvent = new Date(startDate);
    const effectiveStartDate = startDateEvent < now ? now : startDateEvent;

    try {
      const authStatus = await RNCalendarEvents.requestPermissions();
      if (authStatus === 'authorized') {
        const eventId = await RNCalendarEvents.saveEvent(title, {
          startDate: effectiveStartDate.toISOString(),
          endDate: new Date(endDate).toISOString(),
          description: description,
          location: location,
        });

        if (Platform.OS === 'ios') {
          const AddCalendarEvent = require('react-native-add-calendar-event');
          AddCalendarEvent.presentEventViewingDialog({eventId: eventId})
            .then((event: any) => {
              Alert.alert('Calendar', 'Your event has been saved.');
            })
            .catch((error: any) => {
              console.error('Error presenting event viewing dialog', error);
              Alert.alert('Error', 'Failed to open the event viewer: ' + error.message);
            });
        } else {
          Alert.alert('Unsupported Feature', 'This feature is only available on iOS.');
        }
      } else {
        console.error('Calendar permission is not authorized');
        return;
      }
    } catch (error) {
      console.error('Failed to add event to calendar:', error);
    }
  };

  const addCalendarEvent = async (eventConfig: any) => {
    try {
      const AddCalendarEvent = require('react-native-add-calendar-event');
      const eventInfo = await AddCalendarEvent.presentEventCreatingDialog(eventConfig);
      setTimeout(() => {
        if (eventInfo?.action !== 'CANCELED') {
          Alert.alert('Calendar', 'Your event has been saved.');
        } else {
          Alert.alert('Calendar', 'Your event has been cancelled.');
        }
      }, 2000);
    } catch (error) {
      Alert.alert('Error', 'Failed to add event to calendar');
      console.error('Add event error:', error);
    }
  };

  const addToCalendar = () => {
    if (Platform.OS == 'ios') {
      addEventToCalendar(event?.name, event?.start_date, event?.end_date, event?.description, event?.address_name);
    } else {
      const eventConfig = {
        title: event?.name,
        startDate: new Date(event?.start_date || '').toISOString(),
        endDate: new Date(event?.end_date || '').toISOString(),
        location: event?.address_name,
        notes: event?.description,
      };

      addCalendarEvent(eventConfig);
    }
  };

  // Host Chat functionality - exactly like old implementation
  const onHostChatClick = async (host: Business) => {
    setModalVisible(true);
  };

  // Block/Unblock user functionality
  const handleBlockUnblockUser = async () => {
    if (!host?.uid) {
      Alert.alert('Error', 'Unable to block/unblock user - host information not available');
      return;
    }

    const isBlocked = blockStatus?.is_blocked;
    const hostName = (host as Business)?.name || (host as User)?.first_name || 'this user';
    const action = isBlocked ? 'Unblock' : 'Block';
    const actionLower = action.toLowerCase();

    Alert.alert(
      `${action} User`,
      isBlocked
        ? `Are you sure you want to unblock ${hostName}? You will be able to see their events and interact with them again.`
        : `Are you sure you want to block ${hostName}? You won't see their events or be able to interact with them.`,
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: action,
          style: isBlocked ? 'default' : 'destructive',
          onPress: async () => {
            try {
              if (isBlocked) {
                await unblockUser({
                  blocked_id: host.uid,
                  reason: 'Unblocked from event page',
                });
              } else {
                await blockUser({
                  blocked_id: host.uid,
                  reason: 'Blocked from event page',
                });
              }

              // Refresh block status
              refetchBlockStatus();

              Alert.alert(`User ${action}ed`, `User has been ${actionLower}ed successfully`, [
                {
                  text: 'OK',
                  onPress: () => {
                    // Navigate back to previous screen only if blocking
                    if (!isBlocked) {
                      if (isFromSheet) {
                        onClose();
                      } else {
                        navigation.goBack();
                      }
                    }
                  },
                },
              ]);
            } catch (error) {
              console.error(`Error ${actionLower}ing user:`, error);
              Alert.alert('Error', `Failed to ${actionLower} user. Please try again.`);
            }
          },
        },
      ],
    );
  };

  const onIssueTypeClick = async (type: string) => {
    console.log('🔍 onIssueTypeClick called with type:', type);
    console.log('🔍 currentUserId:', currentUserId);
    console.log('🔍 event:', event?.event_id);

    setIsCreatingChat(true);

    try {
      if (!currentUserId) {
        console.error('❌ Cannot create chat: User not authenticated');
        Alert.alert('Authentication Error', 'Please log in to contact support.');
        return;
      }

      const hHost = type == 't' ? pyxiHost : host;
      if (hHost && event) {
        const chatId = await FirebaseChatsService.createOrganisationChat({
          user_id1: currentUserId,
          user_id2: hHost?.uid,
          user_name1: `${userAccount?.first_name} ${userAccount?.last_name || ''}`,
          user_name2: (hHost as Business)?.name
            ? (hHost as Business)?.name
            : (hHost as unknown as User)?.last_name
              ? `${(hHost as unknown as User)?.first_name} ${(hHost as unknown as User)?.last_name || ''}`
              : (hHost as unknown as User)?.first_name || '',
          user_image: userAccount?.photo + '',
          user_image2: hHost?.photo + '',
          event: event,
          isTechnical: type == 't' ? true : false,
          isPyxiSelect: isPyxiSelect,
        });

        // Skip Firestore operations on web platform
        if (Platform.OS !== 'web') {
          const chatRef = firestore().collection('chats').doc(chatId);
          const doc = await chatRef.get();
          if (doc.exists) {
            const chatData = doc.data() as ChatType;
            const updatedMessages = chatData.history.map((message: any) => {
              if (!message.readUserIds?.includes(currentUserId)) {
                console.log('Updating message:', message);
                return {...message, readUserIds: [...(message.readUserIds || []), currentUserId]};
              }
              return message;
            });

            await chatRef.update({history: updatedMessages});
          }
        }

        // Navigate to chat stack
        console.log('🔍 Navigating to CHAT_STACK with chatId:', chatId);
        navigation.navigate(SCREENS.CHAT_STACK, {
          key: chatId,
          fromEventId: event?.event_id,
          fromScreen: 'EventDetails',
          userName: (hHost as Business)?.name
            ? (hHost as Business)?.name
            : (hHost as unknown as User)?.last_name
              ? `${(hHost as unknown as User)?.first_name} ${(hHost as unknown as User)?.last_name || ''}`
              : (hHost as unknown as User)?.first_name || '',
        });
        console.log('🔍 Navigation call completed');
      }
    } catch (error) {
      console.error('🔍 Error in onIssueTypeClick:', error);
      Alert.alert('Error', 'Failed to create chat. Please try again.');
    } finally {
      setIsCreatingChat(false);
    }
  };

  // Comments functionality - exactly like old implementation
  const onCommentPress = () => {
    commentSheetRef.current?.present();
  };

  // Helper functions from original EventDetails.tsx
  const getAttendeesCount = () => {
    let totalAttendees = 1;
    if (eventAttendees && eventAttendees.length > 0) {
      totalAttendees = 1 + eventAttendees.length;
    }
    if (totalAttendees > 2) {
      totalAttendees = totalAttendees - 2;
    }
    return totalAttendees;
  };

  const getLinkOf1stAttendeesProfile = () => {
    if (eventAttendees && eventAttendees.length > 0 && eventAttendees[0].user && eventAttendees[0].user.photo) {
      return eventAttendees[0].user.photo;
    }
    return null;
  };

  const getLinkOf2ndAttendeesProfile = () => {
    if (eventAttendees && eventAttendees.length > 1 && eventAttendees[1].user && eventAttendees[1].user.photo) {
      return eventAttendees[1].user.photo;
    }
    return null;
  };

  const onAddComment = async (comment: string) => {
    const reqBody = {
      comment: comment,
      pin: false,
      parent_id: null,
      event_id: event?.event_id || 0,
    };
    await createComment(reqBody);
    refetchCommentEvent();
  };

  // Event Management Functions - exactly like old implementation
  const handleEditEvent = () => {
    if (!event) {
      return;
    }
    if (categoryData) {
      categoryData.forEach(cate_id => {
        setSubCategory(cate_id.subcategory_id);
      });
    }
    navigation.navigate(SCREENS.CREATE_EVENT_TEMPLATE, {item: event});
  };

  const cancelEvent = async () => {
    setIsLoading(true);
    try {
      await cancelAEvent({event_id: event?.event_id || 0});
      Alert.alert('Success', 'Event has been cancelled successfully');
      navigation.goBack();
    } catch (error) {
      Alert.alert('Error', 'Failed to cancel event');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteEventHandler = async () => {
    Alert.alert('Delete Event', 'Are you sure you want to delete this event? This action cannot be undone.', [
      {
        text: 'Cancel',
        style: 'cancel',
      },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          setIsLoading(true);
          try {
            await deleteEvent({event_id: event?.event_id || 0});
            Alert.alert('Success', 'Event has been deleted successfully');
            navigation.goBack();
          } catch (error) {
            Alert.alert('Error', 'Failed to delete event');
          } finally {
            setIsLoading(false);
          }
        },
      },
    ]);
  };

  // Postal Code Dialog for Greek Events - exactly like old implementation
  const onContinuePress = async () => {
    if (!postCode || postCode.length < 3) {
      Alert.alert('Please enter postal code.');
      return;
    }

    setPinCodeDialog(false);

    const data = await updateUserMutation(postCode);
    if (data.postal_code) {
      joinEvent();
    }
  };

  // Add Join Event Status for payment URL events - exactly like old implementation
  const addJoinEventStatus = async () => {
    setModalIsVisible(false);
    await addEventUserStatus({
      event_id: event.event_id.toString(),
    });
  };

  // Helper functions for event type
  const getEventTypeColor = () => {
    switch (event?.event_type) {
      case 'business':
        return colors.statusPurple;
      case 'private':
        return colors.statusOrange;
      default:
        return colors.primary;
    }
  };

  const getEventTypeLabel = () => {
    switch (event?.event_type) {
      case 'business':
        return 'Business Event';
      case 'private':
        return 'Private Event';
      default:
        return 'Public Event';
    }
  };

  // Find People to Go With handler - THE MOST PROMINENT FEATURE
  const handleMatchingPress = () => {
    // Note: Removed the check for user status as per original implementation
    if (event.pay_per_match) {
      if (isFromSheet) {
        onClose();
      }
      navigation.navigate(SCREENS.LOGISTIC_OPTION, {event: event, userStatus: userStatus});
      return;
    }

    if (matchingStatus?.matches_available && matchingStatus?.type === 'colleague') {
      setCurrentMatchingEvent(event?.event_id || null);
      setRefresh(false);
      openMatchingLoadingModal();
    } else {
      // Check if user has groups before showing bottom sheet
      if (groups && groups.length > 0) {
        matchingOptionsSheetRef.current?.present();
      } else {
        // If no groups, go directly to matching with 'anyone' type
        setCurrentMatchingEvent(event?.event_id || null);
        setDomain(userDomain || null);
        setMatchingType('anyone');
        setRefresh(false);
        openMatchingLoadingModal();
      }
    }
  };

  return (
    <BottomSheetModalProvider>
      <View style={styles.container}>
        <GestureHandlerRootView style={styles.container}>
          {/* Sticky Header Overlay - With back button */}
          <View style={[styles.stickyHeader, {paddingTop: top, height: 60 + top}]}>
            <TouchableOpacity
              onPress={() => {
                console.log('🔄 Sticky header back button pressed!');
                handleCloseWithAnimation();
              }}
              style={styles.improvedStickyBackButton}
              activeOpacity={0.8}
              hitSlop={{top: 15, bottom: 15, left: 15, right: 15}}>
              <Ionicons name="arrow-back" size={24} color={colors.primary} />
            </TouchableOpacity>
            <Text style={styles.stickyTitle} numberOfLines={1}>
              {event.name}
            </Text>
            <View style={styles.stickyPlaceholder} />
          </View>
          {/* Scrollable Content - Positioned absolutely from top */}
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            scrollEventThrottle={16}
            showsVerticalScrollIndicator={false}>
            <View style={styles.gestureContainer}>
              {/* Header Image - Like your previous beautiful design */}
              <View style={styles.headerContainer}>
                <FastImage source={{uri: event.image_url}} style={styles.headerImage} resizeMode="cover" />
                <View style={styles.headerOverlay} />
                {/* Free/Paid Tag - Matching event tiles style */}
                <View
                  style={[
                    styles.priceBadge,
                    {
                      backgroundColor: event.is_paid ? colors.statusOrange : colors.statusGreen,
                    },
                  ]}>
                  <MaterialIcons
                    name={event.is_paid ? 'attach-money' : 'check-circle'}
                    size={16}
                    color={colors.white}
                  />
                  <Text style={styles.priceBadgeText}>{event.is_paid ? 'Paid' : 'Free'}</Text>
                </View>

                {/* Share and Like buttons - Fixed position over header image */}
                <View style={styles.fixedBottomRightActions}>
                  <TouchableOpacity
                    onPress={() => {
                      console.log('🔄 Share button pressed!');
                      handleSharePress();
                    }}
                    style={[styles.actionButton, {backgroundColor: colors.overlayBackground}]}
                    activeOpacity={0.8}>
                    <Ionicons name="share-outline" size={24} color={colors.white} />
                  </TouchableOpacity>
                  <View style={[styles.actionButton, {backgroundColor: colors.overlayBackground}]}>
                    <LikeButton liked={event.user_liked} eventId={event.event_id} />
                  </View>
                </View>
              </View>
            </View>
            {/* Spacer to account for header image */}
            <View style={styles.headerSpacer} />

            {/* Main Content Card */}
            <View style={styles.contentCard}>
              {/* Event Title */}
              <Text style={styles.eventTitle}>{event.name}</Text>

              {/* Event Info Grid */}
              <View style={styles.eventInfoGrid}>
                {/* Date & Time - Clickable to add to calendar */}
                <TouchableOpacity disabled style={styles.infoItem} onPress={() => addToCalendar()}>
                  <View style={styles.infoIcon}>
                    <MaterialIcons name="event" size={24} color={colors.primary} />
                  </View>
                  <View style={styles.infoContent}>
                    <Text style={styles.infoLabel}>Date & Time</Text>
                    <Text style={styles.infoValue}>
                      {event?.start_date ? moment.utc(event?.start_date).format('ddd, MMM DD, YYYY hh:mm A') : 'N/A'}
                    </Text>
                    <Text style={styles.infoValue}>
                      {event?.start_date ? moment.utc(event?.end_date).format('ddd, MMM DD, YYYY hh:mm A') : 'N/A'}
                    </Text>
                    {false && (
                      <View style={{flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center'}}>
                        <Text style={styles.infoSubValue}></Text>
                        <Text
                          style={[
                            styles.calendarHint,
                            {marginLeft: 10, color: colors.statusPurple, fontSize: typography.fontSize.xs},
                          ]}>
                          Click to add to calendar
                        </Text>
                      </View>
                    )}
                  </View>
                </TouchableOpacity>

                {/* Location */}
                <TouchableOpacity style={styles.infoItem} onPress={handleOpenMaps}>
                  <View style={styles.infoIcon}>
                    <MaterialIcons name="location-on" size={24} color={colors.primary} />
                  </View>
                  <View style={styles.infoContent}>
                    <Text style={styles.infoLabel}>Location</Text>
                    <Text style={styles.infoValue}>{event.address_name}</Text>
                    {false && <Text style={[styles.infoSubValue, {color: colors.primary}]}>Tap for directions</Text>}
                  </View>
                </TouchableOpacity>

                {/* Attendees */}
                <View style={styles.infoItem}>
                  <View style={styles.infoIcon}>
                    <MaterialIcons name="people" size={24} color={colors.primary} />
                  </View>
                  <View style={styles.infoContent}>
                    <Text style={styles.infoLabel}>Attendees</Text>

                    {/* If no attendees, show one fake attendee */}
                    {isPyxiSelect ? (
                      <Text style={styles.infoValue}>Small groups of 4-6 people</Text>
                    ) : !eventAttendees || eventAttendees.length <= 0 ? (
                      <View style={styles.attendeeRowStyle}>
                        <Image style={styles.profileImg} source={randomUserImg1} />
                        <Text style={styles.attendeesCount}>+ 1 person attending</Text>
                      </View>
                    ) : (
                      /* If there are attendees, show actual attendee avatars */
                      <View style={styles.attendeeAvatarsRow}>
                        {eventAttendees.slice(0, 3).map((attendee, index) => (
                          <View
                            key={attendee.user_id}
                            style={[styles.attendeeAvatarSmall, {marginLeft: index > 0 ? -8 : 0}]}>
                            {attendee.user?.photo ? (
                              <FastImage
                                source={{uri: attendee.user.photo}}
                                style={styles.attendeeAvatarImage}
                                resizeMode="cover"
                              />
                            ) : (
                              <View style={[styles.attendeeAvatarImage, styles.attendeePlaceholder]}>
                                <Text style={styles.attendeeInitial}>
                                  {(attendee.user?.first_name || attendee.name || 'U').charAt(0).toUpperCase()}
                                </Text>
                              </View>
                            )}
                          </View>
                        ))}
                        {eventAttendees.length > 3 && (
                          <Text style={styles.moreAttendeesText}>+{eventAttendees.length - 3}</Text>
                        )}
                        <Text style={styles.attendeesCount}>+ {getAttendeesCount()} people attending</Text>
                      </View>
                    )}
                  </View>
                </View>

                {/* Price (if paid event) */}
                {event.is_paid && (
                  <View style={styles.infoItem}>
                    <View style={styles.infoIcon}>
                      <MaterialIcons name="attach-money" size={24} color={colors.primary} />
                    </View>
                    <View style={styles.infoContent}>
                      <Text style={styles.infoLabel}>Price</Text>
                      <Text style={styles.infoValue}>Paid Event</Text>
                      <Text style={styles.infoSubValue}>Tickets required</Text>
                    </View>
                  </View>
                )}

                {/* Recurrence (if recurring event) */}
                {event?.event_group_id && (
                  <View style={styles.infoItem}>
                    <View style={styles.infoIcon}>
                      <MaterialIcons name="repeat" size={24} color={colors.primary} />
                    </View>
                    <View style={styles.infoContent}>
                      <Text style={styles.infoLabel}>Recurrence</Text>
                      <Text style={styles.infoValue}>{getMessageOfRecurrence(event?.event_group)}</Text>

                      {/* Upcoming 6 Dates - Quick Date Selection */}
                      {event?.recurrence_events && event.recurrence_events.length > 0 && (
                        <View style={styles.upcomingDatesContainer}>
                          <Text style={styles.upcomingDatesTitle}>Upcoming dates:</Text>
                          <ScrollView
                            horizontal
                            showsHorizontalScrollIndicator={false}
                            style={styles.upcomingDatesScroll}
                            contentContainerStyle={styles.upcomingDatesContent}>
                            {event.recurrence_events
                              .filter(
                                recurrenceEvent =>
                                  // Only show future dates and not cancelled events
                                  // Also exclude events starting within 12 hours
                                  moment(recurrenceEvent.start_date).isAfter(moment()) &&
                                  !recurrenceEvent.is_cancelled &&
                                  moment(recurrenceEvent.start_date).diff(moment(), 'hours') > 12,
                              )
                              .slice(0, 6)
                              .map((recurrenceEvent, index) => (
                                <TouchableOpacity
                                  key={recurrenceEvent.event_id}
                                  style={styles.upcomingDateCard}
                                  onPress={() => {
                                    // User selected a specific date - join that event directly without calendar
                                    haptics.medium();
                                    joinEvent(recurrenceEvent.event_id);
                                  }}
                                  activeOpacity={0.7}>
                                  <View style={styles.upcomingDateHeader}>
                                    <Text style={styles.upcomingDateMonth}>
                                      {moment(recurrenceEvent.start_date).format('MMM')}
                                    </Text>
                                    <Text style={styles.upcomingDateDay}>
                                      {moment(recurrenceEvent.start_date).format('DD')}
                                    </Text>
                                  </View>
                                  <Text style={styles.upcomingDateTime}>
                                    {moment(recurrenceEvent.start_date).format('h:mm A')}
                                  </Text>
                                  <Text style={styles.upcomingDateWeekday}>
                                    {moment(recurrenceEvent.start_date).format('ddd')}
                                  </Text>
                                </TouchableOpacity>
                              ))}

                            {/* Show more button if there are more dates available */}
                            {event.recurrence_events.filter(
                              recurrenceEvent =>
                                moment(recurrenceEvent.start_date).isAfter(moment()) &&
                                !recurrenceEvent.is_cancelled &&
                                moment(recurrenceEvent.start_date).diff(moment(), 'hours') > 12,
                            ).length > 6 && (
                              <TouchableOpacity
                                style={styles.showMoreDateCard}
                                onPress={() => {
                                  // Open full calendar for more date options
                                  haptics.light();
                                  calendarRef.current?.open();
                                }}
                                activeOpacity={0.7}>
                                <MaterialIcons name="more-horiz" size={24} color={colors.primary} />
                                <Text style={styles.showMoreText}>More</Text>
                              </TouchableOpacity>
                            )}
                          </ScrollView>
                        </View>
                      )}
                    </View>
                  </View>
                )}
              </View>

              {/* Event Description */}
              <View style={styles.descriptionSection}>
                <Text style={styles.sectionTitle}>About this event</Text>
                <ReadMore
                  numberOfLines={TRUNCATE_DESCRIPTION_NUMBER_OF_LINES}
                  renderTruncatedFooter={(handlePress: () => void) => (
                    <Text style={{...styles.expandText, color: colors.primary}} onPress={handlePress}>
                      Read more
                    </Text>
                  )}
                  renderRevealedFooter={(handlePress: () => void) => (
                    <Text style={{...styles.expandText, color: colors.primary}} onPress={handlePress}>
                      Read less
                    </Text>
                  )}>
                  <Text style={styles.eventDescription}>
                    {event.description || 'No description available.'}
                  </Text>
                </ReadMore>
              </View>

              {/* MAP SECTION - Like in list view, not full screen */}
              {event?.coords?.lat && event?.coords?.long && (
                <View style={styles.mapSection}>
                  <Text style={styles.sectionTitle}>Location</Text>
                  {isPyxiSelect && (
                    <Text style={{color: colors.textSecondary}}>
                      Exact location is shared 24 hours before the dinner to paid attendees
                    </Text>
                  )}
                  <MapView
                    ref={mapRef}
                    style={styles.mapView}
                    provider={PROVIDER_GOOGLE}
                    showsMyLocationButton={false}
                    showsCompass={true}
                    toolbarEnabled={true}
                    showsBuildings={true}
                    showsIndoors={true}
                    initialRegion={{
                      latitude: event.coords.lat,
                      longitude: event.coords.long,
                      latitudeDelta: 0.04,
                      longitudeDelta: 0.04,
                    }}
                    showsUserLocation={true}
                    scrollEnabled={true}
                    zoomEnabled={true}
                    pitchEnabled={true}
                    rotateEnabled={true}>
                    {!isPyxiSelect && (
                      <Marker
                        coordinate={{
                          latitude: event.coords.lat,
                          longitude: event.coords.long,
                        }}>
                        <View style={styles.customMarkerContainer}>
                          <View style={styles.markerContent}>
                            <Text style={styles.addressText}>{event.address_name}</Text>
                          </View>
                          <View style={styles.arrowContainer}>
                            <View style={styles.arrow} />
                          </View>
                        </View>
                      </Marker>
                    )}
                  </MapView>
                  <TouchableOpacity style={styles.directionsButton} onPress={handleOpenMaps}>
                    <Text style={[styles.directionsText, {color: colors.primary}]}>Get Directions</Text>
                  </TouchableOpacity>
                </View>
              )}

              {/* COMMENTS/UPDATES SECTION */}
              {((commentList && commentList?.length > 0) || isUserEvent) && (
                <View style={styles.commentsSection}>
                  {commentList && commentList?.length > 0 ? (
                    <TouchableOpacity onPress={onCommentPress} style={styles.commentView}>
                      <Text style={styles.commentTitle}>Updates {commentList.length}</Text>
                      <View style={styles.commentItemView}>
                        <FastImage source={{uri: commentList[0].user.photo}} style={styles.userImage} />
                        <Text style={styles.commentTextView}>{commentList[commentList.length - 1].comment}</Text>
                      </View>
                    </TouchableOpacity>
                  ) : (
                    isUserEvent && (
                      <TouchableOpacity onPress={onCommentPress} style={styles.commentView}>
                        <Text style={styles.commentTitle}>Updates</Text>
                        <View style={[styles.commentItemView, {alignItems: 'center'}]}>
                          <FastImage source={{uri: user?.photo}} style={styles.userImage} />
                          <Text style={styles.commentTextView}>Add updates here</Text>
                        </View>
                      </TouchableOpacity>
                    )
                  )}
                </View>
              )}

              {/* JOIN/LEAVE EVENT BUTTON */}

              {/* FIND PEOPLE TO GO WITH - MOST PROMINENT FEATURE */}
              {!isUserEvent && !isPyxiSelect && (
                <View style={styles.findPeopleSection}>
                  <Button
                    label={
                      matchingStatus?.matches_available
                        ? matchingStatus?.type === 'all'
                          ? t('events.see_my_match')
                          : t('home.find_someone')
                        : t('home.find_someone')
                    }
                    disabled={isPyxiSelect}
                    containerStyle={{
                      backgroundColor: isPyxiSelect
                        ? colors.gray400
                        : matchingStatus?.matches_available
                          ? colors.statusGreen
                          : colors.statusPurple,
                      marginTop: 20,
                    }}
                    textStyle={{fontSize: 16, fontWeight: '600', color: colors.white}}
                    onPress={handleMatchingPress}
                  />

                  {/* Find My Colleagues Button for business events */}
                  {(host as Business) && (host as Business)!.user_pooling! && (
                    <Button
                      label={
                        matchingStatus?.matches_available
                          ? matchingStatus?.type === 'colleague'
                            ? 'See My Colleagues'
                            : 'Find My Colleagues'
                          : 'Find My Colleagues'
                      }
                      containerStyle={{
                        backgroundColor: colors.primary,
                        marginTop: 10,
                      }}
                      textStyle={{fontSize: 16, fontWeight: '600', color: colors.white}}
                      onPress={() => {
                        if (matchingStatus?.matches_available && matchingStatus?.type === 'all') {
                          setCurrentMatchingEvent(event?.event_id || null);
                          setRefresh(false);
                          openMatchingLoadingModal();
                        } else {
                          matchingOptionsSheetRef.current?.present();
                        }
                      }}
                    />
                  )}

                  {/* Share Button */}
                  <Button
                    label={t('home.share')}
                    containerStyle={{
                      backgroundColor: colors.white,
                      marginTop: 12,
                      borderWidth: 1,
                      borderColor: colors.statusPurple,
                    }}
                    textStyle={{fontWeight: '500', color: colors.statusPurple}}
                    onPress={handleSharePress}
                  />
                </View>
              )}

              {/* EVENT MANAGEMENT BUTTONS FOR OWNERS */}
              {isUserEvent ? (
                <View style={styles.eventManagementSection}>
                  {event?.is_cancelled ? (
                    <Text style={styles.errorText}>
                      {event?.event_type === 'business'
                        ? 'This event was cancelled by the organiser'
                        : 'This event was cancelled by host.'}
                    </Text>
                  ) : null}

                  <Button
                    label={t('events.update_event')}
                    containerStyle={{backgroundColor: colors.primary, marginTop: 10}}
                    textStyle={{fontSize: 16, fontWeight: '600', color: colors.white}}
                    onPress={handleEditEvent}
                  />

                  <Button
                    label={t('events.cancel_event')}
                    containerStyle={{backgroundColor: colors.error, marginTop: 10}}
                    textStyle={{fontSize: 16, fontWeight: '600', color: colors.white}}
                    onPress={() => cancelEvent()}
                    isLoading={isLoading}
                  />

                  <Button
                    label="Delete Event"
                    containerStyle={{backgroundColor: colors.error, marginTop: 10}}
                    textStyle={{fontSize: 16, fontWeight: '600', color: colors.white}}
                    onPress={deleteEventHandler}
                    isLoading={isLoading}
                  />
                </View>
              ) : null}

              {/* HOST INFORMATION SECTION - EXACTLY like original EventDetails.tsx */}
              {(!!host || event?.host_id) && (
                <View style={styles.hostSectionOld}>
                  <View style={styles.sectionHeader}>
                    <Text style={styles.sectionTitleOld}>{t('events.host')}</Text>
                    {isUserEvent && (
                      <Button
                        onPress={() =>
                          navigation.navigate(SCREENS.PENDING_ATTENDEES, {
                            eventId: event!.event_id,
                            eventName: event!.name,
                          })
                        }
                        label={t('events.requiring_confirmation')}
                        containerStyle={{
                          backgroundColor: colors.statusPurple,
                          borderRadius: 50,
                        }}
                        textStyle={{fontSize: 15, lineHeight: 16, fontWeight: '500', color: colors.white}}
                      />
                    )}
                  </View>

                  <TouchableOpacity
                    style={styles.hostContainer}
                    onPress={() => {
                      navigation.navigate(SCREENS.PERSONAL_INFO, {
                        user: {
                          tag: host?.uid + 'image',
                          source: host?.photo || '',
                          description: host?.description || '',
                          name: (host as Business)?.name
                            ? (host as Business)?.name
                            : (host as User)?.last_name
                              ? `${(host as User)?.first_name} ${(host as User)?.last_name || ''}`
                              : (host as User)?.first_name || '',
                          user_id: host?.uid || '',
                          eventName: event?.name,
                        },
                      });
                    }}>
                    <View style={styles.hostInfo}>
                      <View style={styles.hostPhoto}>
                        <FastImage
                          style={StyleSheet.absoluteFillObject}
                          resizeMode={'cover'}
                          source={{uri: host?.photo, priority: 'normal'}}
                        />
                      </View>

                      <Text style={styles.hostNameOld}>
                        {host
                          ? (host as Business)?.name
                            ? (host as Business)?.name
                            : (host as User)?.last_name
                              ? `${(host as User)?.first_name} ${(host as User)?.last_name || ''}`
                              : (host as User)?.first_name || ''
                          : 'Loading host...'}
                      </Text>

                      {!isUserEvent && (
                        <View style={styles.hostButtonsContainer}>
                          {!blockStatus?.is_blocked && (
                            <Button
                              onPress={() => onHostChatClick(host as Business)}
                              label={isPyxiSelect ? 'Chat With Us' : t('chat.chat')}
                              disabled={isCreatingChat}
                              isLoading={isCreatingChat}
                              containerStyle={{
                                backgroundColor: isCreatingChat ? colors.statusGray : colors.statusPurple,
                                borderRadius: 50,
                                marginRight: spacing.sm,
                              }}
                              textStyle={{fontSize: 15, lineHeight: 16, fontWeight: '500', color: colors.white}}
                            />
                          )}
                          {false && (
                            <Button
                              onPress={handleBlockUnblockUser}
                              label={blockStatus?.is_blocked ? 'Unblock User' : 'Block User'}
                              disabled={isBlockingUser || isUnblockingUser}
                              containerStyle={{
                                backgroundColor: blockStatus?.is_blocked ? colors.statusGreen : colors.error,
                                borderRadius: 50,
                              }}
                              textStyle={{fontSize: 15, lineHeight: 16, fontWeight: '500', color: colors.white}}
                            />
                          )}
                        </View>
                      )}
                    </View>
                  </TouchableOpacity>
                </View>
              )}

              {/* ATTENDEES MANAGEMENT SECTION - FOR HOSTS ONLY */}
              {isUserEvent && (
                <View style={styles.attendeesSection}>
                  <View style={styles.sectionHeader}>
                    <Text style={styles.sectionTitle}>{t('events.attendees')}</Text>
                  </View>
                  <View style={styles.attendeesContainer}>
                    {/* If user is host, show full attendee list with remove button - MOVED HERE */}
                    {eventAttendees && eventAttendees.length > 0 && (
                      <View style={styles.fullAttendeesList}>
                        <Text style={[styles.sectionTitle, {fontSize: 16, marginBottom: spacing.sm}]}>
                          Manage Attendees
                        </Text>
                        {eventAttendees.map?.((attendee, index) => (
                          <View key={index}>
                            <TouchableOpacity
                              style={styles.attendeeContainer}
                              disabled={!attendee.user}
                              onPress={() => {
                                if (isFromSheet) {
                                  onClose();
                                }
                                navigation.navigate(SCREENS.PERSONAL_INFO, {
                                  user: {
                                    tag: index + 'image',
                                    source: attendee.user.photo,
                                    description: attendee.user.description,
                                    name: `${attendee.user.first_name} ${attendee.user.last_name || ''}`,
                                    user_id: attendee.user.uid,
                                    eventName: event?.name,
                                  },
                                });
                              }}>
                              <View style={styles.attendeeLeftContainer}>
                                {attendee?.user && (
                                  <View style={styles.attendeePhoto}>
                                    <FastImage
                                      style={StyleSheet.absoluteFillObject}
                                      resizeMode={'cover'}
                                      source={{uri: attendee?.user?.photo, priority: 'normal'}}
                                    />
                                  </View>
                                )}
                                {attendee?.user ? (
                                  <Text
                                    style={
                                      styles.attendeeName
                                    }>{`${attendee?.user?.first_name} ${attendee?.user?.last_name}`}</Text>
                                ) : (
                                  <Text style={[styles.attendeeName, {marginLeft: 0}]}>
                                    {' '}
                                    {`${attendee?.name}`} <Text style={styles.attendeeSub}>{'(external)'}</Text>
                                  </Text>
                                )}
                              </View>
                              <Button
                                label={t('generic.remove')}
                                onPress={async () => {
                                  await deleteEventUserStatus({
                                    event_id: event.event_id,
                                    user_id: attendee.user?.uid,
                                    email: attendee.email,
                                  });
                                  refetchAttendees(); // Refresh the list after removal
                                }}
                                containerStyle={styles.buttonRejectContainer}
                                textStyle={styles.buttonText}
                              />
                            </TouchableOpacity>
                            <View style={styles.divider} />
                          </View>
                        ))}
                      </View>
                    )}
                  </View>
                </View>
              )}
            </View>

            {/* Bottom Spacing for Scroll */}
            <View style={{height: 100}} />
          </ScrollView>

          {/* COMMENT SHEET MODAL */}
          <CommentSheet
            commentSheetRef={commentSheetRef}
            commentList={commentList || []}
            onAddComment={onAddComment}
            photo={user?.photo}
            isUserEvent={isUserEvent}
          />

          {/* POSTAL CODE DIALOG FOR GREEK EVENTS */}
          <Dialog
            visible={pincodeDialogVisible}
            contentStyle={{padding: 0}}
            contentInsetAdjustmentBehavior="always"
            titleStyle={{
              alignSelf: 'flex-start',
              fontSize: 14,
              fontWeight: 'bold',
            }}
            title="Please enter post code"
            onTouchOutside={() => setPinCodeDialog(false)}
            onRequestClose={() => setPinCodeDialog(false)}>
            <View
              style={{
                paddingHorizontal: 15,
                paddingBottom: 15,
              }}>
              <TextInput
                placeholder="Enter post code"
                value={postCode}
                style={styles.textInput}
                onChangeText={setPostCode}
              />
              <Button
                label={'Continue'}
                containerStyle={{backgroundColor: colors.primary, marginTop: 10}}
                textStyle={{color: colors.white}}
                onPress={onContinuePress}
              />
            </View>
          </Dialog>

          {/* ISSUE REPORTING MODAL */}
          <IssueModal
            visible={modalVisible}
            onClose={() => !isCreatingChat && setModalVisible(false)}
            onTechnicalIssueClick={async () => {
              if (isCreatingChat) return;
              console.log('Technical Issue Selected');
              setModalVisible(false);
              await onIssueTypeClick('t');
            }}
            onEventIssueClick={async () => {
              if (isCreatingChat) return;
              setModalVisible(false);
              await onIssueTypeClick('e');
            }}
          />

          {/* CHAT CREATION LOADING OVERLAY */}
          {isCreatingChat && (
            <View style={styles.chatLoadingOverlay}>
              <View style={styles.chatLoadingContainer}>
                <ModernSpinner size={40} variant="gradient" />
                <Text style={styles.chatLoadingText}>Creating chat...</Text>
              </View>
            </View>
          )}

          {/* MATCHING OPTIONS SHEET */}
          <BottomSheetModal
            ref={matchingOptionsSheetRef}
            snapPoints={['40%']}
            enablePanDownToClose
            backdropComponent={() => (
              <View style={{...StyleSheet.absoluteFillObject, backgroundColor: colors.overlayBackground}} />
            )}>
            <View style={styles.matchingOptionsContainer}>
              <Text style={styles.matchingOptionsTitle}>Find people to go with</Text>
              <TouchableOpacity
                style={styles.matchingOption}
                onPress={() => {
                  setCurrentMatchingEvent(event?.event_id || null);
                  setDomain(userDomain || null);
                  setMatchingType('anyone');
                  setRefresh(false);
                  matchingOptionsSheetRef.current?.dismiss();
                  openMatchingLoadingModal();
                }}>
                <Text style={styles.matchingOptionText}>Anyone on Pyxi</Text>
              </TouchableOpacity>
              {groups && groups.length > 0 && (
                <TouchableOpacity
                  style={styles.matchingOption}
                  onPress={() => {
                    setCurrentMatchingEvent(event?.event_id || null);
                    setDomain(null);
                    setMatchingType('neighbours');
                    setRefresh(false);
                    matchingOptionsSheetRef.current?.dismiss();
                    openMatchingLoadingModal();
                  }}>
                  <Text style={styles.matchingOptionText}>My Neighbours</Text>
                </TouchableOpacity>
              )}
            </View>
          </BottomSheetModal>

          {/* ANIMATED LOADING MODAL FOR QR CODE */}
          <AnimatedLoadingModal
            isVisible={isAnimationVisible}
            isButtonVisible={false}
            callback={() => setAnimationVisisble(false)}
            close={() => setAnimationVisisble(false)}
            isQRCode={true}
          />

          {/* ANIMATED LOADING MODAL FOR MATCHING */}
          <AnimatedLoadingModal
            isVisible={isMatchingLoadingModalVisible}
            callback={matchingModalCallback}
            isButtonVisible={noMatchesButtonIsVisible}
            close={closeMatchingLoadingModal}
            onCloseModal={() => {
              if (isFromSheet) {
                setTimeout(() => {
                  onClose();
                }, 1000);
              }
            }}
          />

          {/* CALENDAR MODAL FOR RECURRING EVENTS */}
          <Calendar
            refRBSheet={calendarRef}
            recurrences={event?.recurrence_events || []}
            eventId={event?.event_id.toString()}
            eventType={event?.event_service_type || 'unknown'}
            onJoinEvent={async (selectedEventId: number | undefined, isLeave, selectedEvent) => {
               const hourLimit = hasActivePlan ? (activePlan?.plan.last_minute_join_hours || 2) : 12;
    
    if (isWithin12HoursOfStart(selectedEvent?.start_date, isPyxiSelect)) {
      Alert.alert('Event Unavailable', `This event is no longer accepting registrations as it starts within ${hourLimit} hours.`);
      return;
    }
              try {
                if (quizAfterDateSelection) {
                  setQuizAfterDateSelection(false);
                  const form_type = event.event_service_type == 'THE_REST_TABLE' ? 'reset_table' : 
                                   event.event_service_type == 'HOUSEWARMING' ? 'housewarming' : 'cultural_edit';
                  
                  console.log('🔍 CALENDAR QUIZ NAVIGATION:', {
                    event_service_type: event.event_service_type,
                    form_type: form_type,
                    selectedEventId: selectedEventId
                  });
                  
                  if (isFromSheet) {
                    onClose();
                  }
                  navigation.navigate(SCREENS.PYXI_SELECT_QUIZ as any, {
                    form_type: form_type,
                    fromEvent: true,
                    event: event,
                    selectedRecurrenceEventId: selectedEventId,
                  });
                } else {
                  await joinEvent(selectedEventId);
                }
              } catch (err) {
                console.error('Error handling calendar selection:', err);
              }
            }}
          />

          {/* MODAL WITH JOIN FOR PAYMENT URL EVENTS */}
          <ModalWithJoin
            name={event?.name || ''}
            description={event?.description || ''}
            isVisible={modalIsVisible}
            close={() => setModalIsVisible(false)}
            payment_url={event?.payment_url || ''}
            onPress={() => () => {
              addJoinEventStatus();
            }}
          />

          {/* PAY PER MATCH MODAL */}
          <PayPerMatchModal
            visible={payPerMatchModalVisible}
            event={event}
            onClose={handlePayPerMatchClose}
            isLoading={payPerMatchLoading}
          />

          {/* PAY PER MATCH MODAL */}
          <PayPerMatchCongratulationModal
            visible={payPerMatchCongratulationModalVisible}
            event={event}
            onFindSomeone={onFindSomeonePayForMatch}
            onClose={handlePayPerMatchCongratulationClose}
            onGetTicket={onGetPayPerMatchTicket}
            isLoading={payPerMatchLoading}
          />

          {/* Join Button - Updated with new logic based on original EventDetails.tsx */}
          {event?.host_id !== currentUserId && !userStatusIsLoading && !userStatusIsFetching && (
            <View style={styles.joinButtonContainer}>
              {statusStr === 'accepted' && !isPyxiSelect ? (
                // User has joined - show "Find someone to go with" button
                <TouchableOpacity
                  style={[styles.joinButton, {backgroundColor: colors.statusPurple}]}
                  onPress={handleMatchingPress}
                  activeOpacity={0.9}>
                  <Text style={styles.joinButtonText}>
                    {matchingStatus?.matches_available
                      ? matchingStatus?.type === 'all'
                        ? t('events.see_my_match')
                        : t('home.find_someone')
                      : t('home.find_someone')}
                  </Text>
                </TouchableOpacity>
              ) : (
                // User hasn't joined - show join/buy button
                <>
                  {event?.host_id !== currentUserId && !userStatusIsLoading && !userStatusIsFetching ? (
                    <View style={styles.joinSection}>
                      {!isEventPassed || userStatus?.status ? (
                        <Button
                          disabled={
                            statusStr === 'pending' ||
                            event?.is_cancelled ||
                            moment(event?.end_date).isBefore(moment()) ||
                            (isWithin12HoursOfStart(event?.start_date, isPyxiSelect) && !event?.event_group_id) ||
                            (event.pay_per_match && !!isJoied)
                          }
                          icon={
                            statusStr === 'accepted' ? (
                              <CheckIcon width={30} height={30} color={'#fff'} />
                            ) : statusStr === 'pending' ? (
                              <BigPendingIcon width={'30'} height={'30'} />
                            ) : undefined
                          }
                          label={
                            statusStr === 'accepted'
                              ? 'I’m confirmed for this event'
                              : statusStr === 'pending'
                                ? 'Waiting for confirmation'
                                : statusStr === 'waiting'
                                  ? 'Waitlisted'
                                  : isWithin12HoursOfStart(event?.start_date, isPyxiSelect) && !event?.event_group_id
                                    ? 'Sold Out'
                                    : event?.is_paid || event?.payment_url
                                      ? 'Book your seat'
                                      : event.pay_per_match && isJoied
                                        ? 'Joined'
                                        : 'Join'
                          }
                          containerStyle={{
                            backgroundColor: event?.is_cancelled
                              ? colors.statusGray
                              : statusStr === 'accepted'
                                ? colors.statusGreen
                                : statusStr === 'pending'
                                  ? colors.primary
                                  : statusStr === 'waiting'
                                    ? colors.primary
                                    : isWithin12HoursOfStart(event?.start_date, isPyxiSelect) && !event?.event_group_id
                                      ? colors.gray400
                                      : event.pay_per_match && isJoied
                                        ? colors.gray400
                                        : colors.statusPurple,
                            marginTop: 10,
                            borderRadius: 20,
                          }}
                          textStyle={{
                            fontSize: statusStr === 'waiting' ? 12 : 16,
                            fontWeight: '600',
                            textAlign: 'center',
                            color: colors.white,
                            lineHeight: 20,
                          }}
                          onPress={async () => onJoinEventClick()}
                        />
                      ) : (
                        <Button
                          label={'Book your seat'}
                          containerStyle={{
                            backgroundColor: colors.gray400,
                            marginTop: 20,
                            borderRadius: 20,
                          }}
                          textStyle={{
                            fontSize: 14,
                            fontWeight: '600',
                            textAlign: 'center',
                            color: colors.white,
                            lineHeight: 20,
                          }}
                          onPress={async () => Alert.alert('The event has ended')}
                        />
                      )}
                    </View>
                  ) : (
                    (userStatusIsLoading || userStatusIsFetching) && (
                      <View style={styles.loaderJoinBtnView}>
                        <ModernSpinner size={24} variant="circular" />
                      </View>
                    )
                  )}
                </>
              )}
            </View>
          )}
        </GestureHandlerRootView>
      </View>
    </BottomSheetModalProvider>
  );
};

const createStyles = (colors: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    floatingBackButton: {
      position: 'absolute',
      left: spacing.md,
      zIndex: 150, // Higher z-index to be on top of everything
    },
    floatingBackButtonTouchable: {
      width: 54,
      height: 54,
      borderRadius: 27,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    floatingBackButtonInner: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: '#000',
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      elevation: 5,
    },
    gestureContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 10,
    },
    headerContainer: {
      height: HEADER_HEIGHT, // Use the reduced height
      position: 'relative',
    },
    headerImage: {
      width: '100%',
      height: '100%',
    },
    headerOverlay: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: colors.overlayBackground,
    },
    headerActions: {
      position: 'absolute',
      left: spacing.md,
      right: spacing.md,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      zIndex: 100, // High z-index to ensure clickability
    },
    rightActions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    actionButton: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: colors.overlayBackground,
      alignItems: 'center',
      justifyContent: 'center',
    },
    improvedActionButton: {
      width: 54,
      height: 54,
      borderRadius: 27,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
    },
    actionButtonInner: {
      width: 44,
      height: 44,
      borderRadius: 22,
      backgroundColor: colors.overlayBackground,
      alignItems: 'center',
      justifyContent: 'center',
      shadowColor: colors.black,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      elevation: 5,
    },
    eventTypeBadge: {
      position: 'absolute',
      bottom: spacing.lg,
      left: spacing.md,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: borderRadius.full,
    },
    eventTypeBadgeText: {
      color: colors.white,
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.bold,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    priceBadge: {
      position: 'absolute',
      bottom: spacing.lg,
      left: spacing.md,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: borderRadius.lg,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.xs,
    },
    priceBadgeText: {
      color: colors.white,
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.bold,
      lineHeight: typography.fontSize.base * typography.lineHeight.tight,
    },
    fixedBottomRightActions: {
      position: 'absolute',
      bottom: spacing.lg,
      right: spacing.lg,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      zIndex: 1000,
      elevation: 10,
    },
    stickyHeader: {
      position: 'absolute',
      left: 0,
      right: 0,
      height: 60,
      backgroundColor: colors.background,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      zIndex: 50, // Below header actions (100) but above content
    },
    stickyBackButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: 'transparent',
      alignItems: 'center',
      justifyContent: 'center',
    },
    improvedStickyBackButton: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: 'transparent',
      alignItems: 'center',
      justifyContent: 'center',
    },
    stickyTitle: {
      flex: 1,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      textAlign: 'center',
      marginHorizontal: spacing.md,
    },
    stickyPlaceholder: {
      width: 40,
      height: 40,
    },
    bottomRightActions: {
      position: 'absolute',
      bottom: spacing.lg,
      right: spacing.lg,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      zIndex: 10,
    },
    scrollView: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    scrollContent: {
      paddingBottom: spacing.xl, // Just bottom padding for content
    },
    headerSpacer: {
      height: HEADER_HEIGHT - borderRadius.xl, // Space for header minus overlap
      position: 'relative',
    },
    scrollableBottomRightActions: {
      position: 'absolute',
      bottom: spacing.lg,
      right: spacing.lg,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
      zIndex: 1000,
      elevation: 10,
    },
    contentCard: {
      backgroundColor: colors.background,
      borderTopLeftRadius: borderRadius.xl,
      borderTopRightRadius: borderRadius.xl,
      paddingHorizontal: spacing.lg,
      paddingTop: spacing.lg,
      minHeight: SCREEN_HEIGHT,
    },
    eventTitle: {
      fontSize: typography.fontSize['3xl'],
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      lineHeight: typography.fontSize['3xl'] * 1.2,
      marginBottom: spacing.md,
    },
    hostSection: {
      marginBottom: spacing.xl,
    },
    hostLabel: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
    },
    hostName: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.textPrimary,
    },
    infoSection: {
      marginBottom: spacing.lg,
    },
    infoRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
    },
    iconContainer: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: colors.gray100,
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.md,
    },
    infoContent: {
      flex: 1,
      paddingTop: spacing.xs,
    },
    infoTitle: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.textPrimary,
      marginBottom: spacing.xs,
    },
    infoText: {
      fontSize: typography.fontSize.base,
      color: colors.textPrimary,
      lineHeight: typography.fontSize.base * 1.4,
    },
    infoSubtext: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginTop: spacing.xs,
    },
    directionsButton: {
      marginTop: spacing.sm,
    },
    directionsText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    descriptionSection: {
      marginTop: spacing.xl,
      marginBottom: spacing.xl,
    },
    mapSection: {
      marginBottom: spacing.xl,
    },
    mapView: {
      height: 300,
      borderRadius: borderRadius.lg,
      marginTop: spacing.md,
      marginBottom: spacing.sm,
    },
    customMarkerContainer: {
      alignItems: 'center',
    },
    markerContent: {
      backgroundColor: colors.primary,
      paddingHorizontal: spacing.sm,
      paddingVertical: spacing.xs,
      borderRadius: borderRadius.sm,
      maxWidth: 200,
    },
    addressText: {
      color: colors.white,
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      textAlign: 'center',
    },
    arrowContainer: {
      alignItems: 'center',
      marginTop: -1,
    },
    arrow: {
      width: 0,
      height: 0,
      borderLeftWidth: 8,
      borderRightWidth: 8,
      borderTopWidth: 8,
      borderLeftColor: 'transparent',
      borderRightColor: 'transparent',
      borderTopColor: colors.primary,
    },
    sectionTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      marginBottom: spacing.md,
    },
    descriptionText: {
      fontSize: typography.fontSize.base,
      color: colors.textPrimary,
      lineHeight: typography.fontSize.base * 1.5,
    },
    expandButton: {
      marginTop: spacing.sm,
    },
    expandText: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    attendeesContainer: {
      marginTop: spacing.md,
      flexDirection: 'row',
      alignItems: 'center',
    },
    attendeesAvatars: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    attendeeAvatar: {
      width: 32,
      height: 32,
      borderRadius: 16,
      borderWidth: 2,
      borderColor: colors.white,
    },
    attendeeImage: {
      width: '100%',
      height: '100%',
      borderRadius: 14,
    },
    attendeePlaceholder: {
      backgroundColor: colors.gray300,
      alignItems: 'center',
      justifyContent: 'center',
    },
    attendeeInitial: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.bold,
      color: colors.white,
    },
    moreAttendeesContainer: {
      backgroundColor: colors.gray400,
      alignItems: 'center',
      justifyContent: 'center',
    },
    moreAttendeesText: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.bold,
      color: colors.white,
    },
    joinButtonContainer: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      backgroundColor: colors.background,
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.md,
      borderTopWidth: 1,
      borderTopColor: colors.border,
    },
    joinButton: {
      height: 56,
      borderRadius: borderRadius.lg,
      alignItems: 'center',
      justifyContent: 'center',
    },
    joinButtonText: {
      color: colors.white,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
    },
    eventInfoGrid: {
      marginBottom: spacing.xl,
    },
    infoItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: spacing.md,
      borderBottomWidth: 1,
      borderBottomColor: colors.border + '20',
    },
    calendarHint: {
      fontSize: typography.fontSize.xs,
      color: colors.textSecondary,
      marginTop: spacing.xs,
      fontStyle: 'italic',
    },
    infoIcon: {
      width: 40,
      height: 40,
      borderRadius: 20,
      backgroundColor: colors.primary + '15',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: spacing.md,
    },
    infoContentOld: {
      flex: 1,
    },
    infoLabel: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
    },
    infoValue: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
      marginBottom: spacing.xs,
    },
    infoSubValue: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
    },
    attendeeAvatarsRow: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: spacing.xs,
    },
    attendeeAvatarSmall: {
      width: 28,
      height: 28,
      borderRadius: 14,
      borderWidth: 2,
      borderColor: colors.surface,
      overflow: 'hidden',
    },
    attendeeAvatarImage: {
      width: '100%',
      height: '100%',
      borderRadius: 12,
    },
    attendeePlaceholderOld: {
      backgroundColor: colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    attendeeInitialOld: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.bold,
      color: colors.white,
    },
    moreAttendeesTextOld: {
      fontSize: typography.fontSize.xs,
      color: colors.textSecondary,
      marginLeft: spacing.sm,
      fontWeight: typography.fontWeight.medium,
    },
    descriptionSectionOld: {
      marginBottom: spacing.xl,
      paddingTop: spacing.lg,
      borderTopWidth: 1,
      borderTopColor: colors.border + '30',
    },
    sectionTitleOld: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.md,
    },
    eventDescription: {
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 24,
    },
    expandButtonOld: {
      marginTop: spacing.sm,
    },
    expandTextOld: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
    },
    joinSection: {
      borderTopWidth: 1,
      borderTopColor: colors.border + '30',
      marginBottom: 10,
    },
    loaderJoinBtnView: {
      alignItems: 'center',
      justifyContent: 'center',
      marginTop: spacing.lg,
      height: 50,
    },
    findPeopleSection: {
      marginTop: spacing.lg,
      paddingTop: spacing.lg,
      borderTopWidth: 1,
      borderTopColor: colors.border + '30',
    },
    hostSectionOld: {
      marginTop: spacing.xl,
      paddingTop: spacing.lg,
      borderTopWidth: 1,
      borderTopColor: colors.border + '30',
    },
    sectionHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing.md,
    },
    hostContainer: {
      backgroundColor: colors.surfaceVariant + '50',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      marginTop: spacing.sm,
    },
    hostInfo: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
    },
    hostButtonsContainer: {
      alignItems: 'center',
      gap: 5,
    },
    hostPhoto: {
      width: 56,
      height: 56,
      borderRadius: 28,
      overflow: 'hidden',
      borderWidth: 2,
      borderColor: colors.primary + '20',
    },
    hostNameOld: {
      flex: 1,
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
    },
    attendeesSection: {
      marginTop: spacing.xl,
      paddingTop: spacing.lg,
      borderTopWidth: 1,
      borderTopColor: colors.border + '30',
    },
    attendeesContainerOld: {
      marginTop: spacing.md,
    },
    attendeeRowStyle: {
      flexDirection: 'row',
      alignItems: 'center',
      marginBottom: spacing.md,
      backgroundColor: colors.surfaceVariant + '30',
      padding: spacing.md,
      borderRadius: borderRadius.lg,
    },
    fullAttendeesList: {
      marginTop: spacing.md,
      backgroundColor: colors.surfaceVariant + '20',
      borderRadius: borderRadius.lg,
      padding: spacing.md,
    },
    profileImg: {
      width: 40,
      height: 40,
      borderRadius: 20,
      borderWidth: 2,
      borderColor: colors.surface,
    },
    attendeesCount: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      marginLeft: spacing.sm,
      fontWeight: typography.fontWeight.medium,
    },
    attendeeContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingVertical: spacing.md,
      backgroundColor: colors.surfaceVariant + '20',
      borderRadius: borderRadius.md,
      paddingHorizontal: spacing.md,
      marginBottom: spacing.sm,
    },
    attendeeLeftContainer: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.md,
    },
    attendeePhoto: {
      width: 48,
      height: 48,
      borderRadius: 24,
      overflow: 'hidden',
      borderWidth: 2,
      borderColor: colors.primary + '20',
    },
    attendeeName: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.semibold,
      color: colors.text,
    },
    attendeeSub: {
      fontSize: typography.fontSize.sm,
      color: colors.textSecondary,
      fontStyle: 'italic',
    },
    buttonRejectContainer: {
      backgroundColor: colors.error,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      borderRadius: borderRadius.lg,
    },
    buttonText: {
      fontSize: typography.fontSize.sm,
      color: colors.white,
      fontWeight: typography.fontWeight.semibold,
    },
    divider: {
      height: 1,
      backgroundColor: colors.border + '30',
      marginVertical: spacing.xs,
    },
    commentsSection: {
      marginTop: spacing.xl,
      paddingTop: spacing.lg,
      borderTopWidth: 1,
      borderTopColor: colors.border + '30',
    },
    commentView: {
      backgroundColor: colors.surfaceVariant + '40',
      borderRadius: borderRadius.xl,
      padding: spacing.lg,
      borderWidth: 1,
      borderColor: colors.border + '20',
    },
    commentTitle: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.md,
    },
    commentItemView: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: spacing.md,
    },
    userImage: {
      width: 44,
      height: 44,
      borderRadius: 22,
      borderWidth: 2,
      borderColor: colors.primary + '20',
    },
    commentTextView: {
      flex: 1,
      fontSize: typography.fontSize.base,
      color: colors.textSecondary,
      lineHeight: 22,
    },
    eventManagementSection: {
      marginTop: spacing.xl,
      paddingTop: spacing.lg,
      borderTopWidth: 1,
      borderTopColor: colors.border + '30',
    },
    errorText: {
      fontSize: typography.fontSize.base,
      color: colors.error,
      textAlign: 'center',
      marginBottom: spacing.md,
      fontWeight: typography.fontWeight.semibold,
      backgroundColor: colors.error + '10',
      padding: spacing.md,
      borderRadius: borderRadius.md,
    },
    textInput: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: borderRadius.lg,
      padding: spacing.md,
      fontSize: typography.fontSize.base,
      color: colors.text,
      backgroundColor: colors.surface,
    },
    matchingOptionsContainer: {
      padding: spacing.xl,
    },
    matchingOptionsTitle: {
      fontSize: typography.fontSize.xl,
      fontWeight: typography.fontWeight.bold,
      color: colors.text,
      marginBottom: spacing.xl,
      textAlign: 'center',
    },
    matchingOption: {
      backgroundColor: colors.primary,
      padding: spacing.lg,
      borderRadius: borderRadius.xl,
      marginBottom: spacing.md,
      alignItems: 'center',
      shadowColor: colors.black,
      shadowOffset: {width: 0, height: 2},
      shadowOpacity: 0.1,
      shadowRadius: 4,
      elevation: 3,
    },
    matchingOptionText: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.semibold,
      color: colors.white,
    },
    chatLoadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: colors.overlayBackground,
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
    },
    chatLoadingContainer: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.xl,
      padding: spacing.xl,
      alignItems: 'center',
      shadowColor: colors.black,
      shadowOffset: {width: 0, height: 4},
      shadowOpacity: 0.3,
      shadowRadius: 8,
      elevation: 8,
    },
    chatLoadingText: {
      fontSize: typography.fontSize.base,
      fontWeight: typography.fontWeight.medium,
      color: colors.textPrimary,
      marginTop: spacing.md,
    },
    upcomingDatesContainer: {
      marginTop: spacing.md,
    },
    upcomingDatesTitle: {
      fontSize: typography.fontSize.sm,
      fontWeight: typography.fontWeight.medium,
      color: colors.textSecondary,
      marginBottom: spacing.sm,
    },
    upcomingDatesScroll: {
      marginTop: spacing.xs,
    },
    upcomingDatesContent: {
      paddingRight: spacing.md,
    },
    upcomingDateCard: {
      backgroundColor: colors.surface,
      borderRadius: borderRadius.md,
      padding: spacing.sm,
      marginRight: spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 80,
      borderWidth: 1,
      borderColor: colors.border + '30',
      shadowColor: colors.black,
      shadowOffset: {width: 0, height: 1},
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    },
    upcomingDateHeader: {
      alignItems: 'center',
      marginBottom: spacing.xs,
    },
    upcomingDateMonth: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.medium,
      color: colors.primary,
      textTransform: 'uppercase',
    },
    upcomingDateDay: {
      fontSize: typography.fontSize.lg,
      fontWeight: typography.fontWeight.bold,
      color: colors.textPrimary,
      lineHeight: typography.fontSize.lg * 1.1,
    },
    upcomingDateTime: {
      fontSize: typography.fontSize.xs,
      color: colors.textSecondary,
      marginBottom: spacing.xs,
    },
    upcomingDateWeekday: {
      fontSize: typography.fontSize.xs,
      color: colors.textTertiary,
      textTransform: 'uppercase',
    },
    showMoreDateCard: {
      backgroundColor: colors.primary + '15',
      borderRadius: borderRadius.md,
      padding: spacing.sm,
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 80,
      borderWidth: 1,
      borderColor: colors.primary + '30',
    },
    showMoreText: {
      fontSize: typography.fontSize.xs,
      fontWeight: typography.fontWeight.medium,
      color: colors.primary,
      marginTop: spacing.xs,
    },
  });

export default CompleteModernEventDetail;
